import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, UserCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Table } from '../../../components/ui/Table';
import { Badge } from '../../../components/ui/Badge';
import { FilterBar } from '../../../components/ui/FilterBar';
import { KpiCard } from '../../../components/ui/KpiCard';
import { Modal } from '../../../components/ui/Modal';
import { ConfirmModal } from '../../../components/ui/ConfirmModal';
import { Input, Select, FormField } from '../../../components/ui/Input';
import { Avatar } from '../../../components/ui/Avatar';
import api from '../../../services/api';
import { useDebounce } from '../../../hooks/useDebounce';
import {
  PER_PAGE, ROLE_VARIANTS, INVITE_DEFAULTS,
  fmtDate, userInitials,
  PageHeader, RowActions, FormFooter, GlobalError, ErrorBanner,
} from '../shared';

const USER_DEFAULTS = {
  fullname: '', initials: '', title: '',
  email: '', password: '', role: 'operations', isActive: true, lang: 'fr',
};

export default function UsersView() {
  const { user: currentUser, hasPermission, loading: authLoading } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !hasPermission('view_users') && !hasPermission('manage_users')) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, hasPermission, navigate]);

  const locale = i18n.language;

  const [roles, setRoles] = useState([]);

  useEffect(() => {
    api.get('/roles').then((r) => setRoles(r.data)).catch(() => {});
  }, []);

  const ROLE_META = useMemo(() => {
    const meta = {};
    roles.forEach((role) => {
      meta[role.name] = {
        label: t(`roles.${role.name}`, { defaultValue: role.description || role.name }),
        variant: ROLE_VARIANTS[role.name] || 'info',
      };
    });
    return meta;
  }, [roles, t]);

  const [users, setUsers]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [counts, setCounts]         = useState({ all: 0, active: 0, pending: 0, admins: 0, byRole: {} });
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage]             = useState(1);
  const [formModal, setFormModal]   = useState(null);
  const [formData, setFormData]     = useState(USER_DEFAULTS);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving]         = useState(false);
  const [confirm, setConfirm]       = useState(null);
  const [inviteModal, setInviteModal]   = useState(false);
  const [inviteData, setInviteData]     = useState(INVITE_DEFAULTS);
  const [inviteErrors, setInviteErrors] = useState({});
  const [inviting, setInviting]         = useState(false);
  const [inviteSent, setInviteSent]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), limit: String(PER_PAGE) });
    if (roleFilter !== 'all') params.set('role', roleFilter);
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    api.get(`/users?${params}`)
      .then((r) => {
        setUsers(r.data.data ?? []);
        setTotal(r.data.total ?? 0);
        if (r.data.counts) setCounts(r.data.counts);
      })
      .catch((e) => setError(e.message || t('common.error_occurred')))
      .finally(() => setLoading(false));
  }, [roleFilter, debouncedSearch, page, t]);

  useEffect(() => { load(); }, [load]);

  function openAdd() {
    setInviteData({ email: '', role: roles[0]?.name || '' });
    setInviteErrors({});
    setInviteSent(false);
    setInviteModal(true);
  }

  function openEdit(row) {
    setFormData({
      fullname:  row.fullname  || '',
      initials:  row.initials  || '',
      title:     row.title     || '',
      email:     row.email     || '',
      password:  '',
      role:      row.role?.name ?? row.role ?? 'operations',
      isActive:  row.isActive  ?? true,
      lang:      row.lang      || 'fr',
    });
    setFormErrors({});
    setFormModal({ mode: 'edit', id: row._id });
  }

  function field(key, val) {
    setFormData((d) => ({ ...d, [key]: val }));
    if (formErrors[key]) setFormErrors((e) => ({ ...e, [key]: undefined }));
  }

  function validateEdit() {
    const errs = {};
    if (!formData.fullname.trim()) errs.fullname = t('common.required_field');
    if (!formData.email.trim())    errs.email    = t('common.required_field');
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSave() {
    if (!validateEdit()) return;
    setSaving(true);
    try {
      const { password, ...update } = formData;
      await api.put(`/users/${formModal.id}`, update);
      setFormModal(null);
      load();
    } catch (e) {
      setFormErrors({ _global: e.response?.data?.message || e.message || t('common.error_occurred') });
    } finally {
      setSaving(false);
    }
  }

  async function handleInvite() {
    const errs = {};
    if (!inviteData.email.trim()) errs.email = t('common.required_field');
    setInviteErrors(errs);
    if (Object.keys(errs).length) return;

    setInviting(true);
    try {
      await api.post('/users/invite', inviteData);
      setInviteSent(true);
      load();
    } catch (e) {
      setInviteErrors({ _global: e.response?.data?.message || e.message || t('common.error_occurred') });
    } finally {
      setInviting(false);
    }
  }

  async function handleDelete() {
    if (!confirm) return;
    setSaving(true);
    try {
      await api.delete(`/users/${confirm._id}`);
      setConfirm(null);
      load();
    } catch {
      // stay open on error
    } finally {
      setSaving(false);
    }
  }

  const columns = useMemo(() => [
    {
      key: 'fullname',
      label: t('users.col_user'),
      render: (_, row) => (
        <div className="flex items-center gap-2.5">
          <Avatar initials={userInitials(row)} size="sm" />
          <div>
            <p className="font-medium text-slate-800">{row.fullname || row.email}</p>
            {row.title && <p className="text-[11px] text-slate-400">{row.title}</p>}
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      label: t('users.col_email'),
      render: (v) => <span className="text-slate-600 text-[12px]">{v || '—'}</span>,
    },
    {
      key: 'role',
      label: t('users.col_role'),
      render: (v) => {
        const roleName = v?.name ?? v;
        return roleName
          ? <Badge variant={ROLE_META[roleName]?.variant || 'info'}>{ROLE_META[roleName]?.label || roleName}</Badge>
          : <span className="text-slate-400">—</span>;
      },
    },
    {
      key: 'invitationStatus',
      label: t('users.col_status'),
      render: (v, row) => {
        if (v === 'pending')  return <Badge variant="warning">{t('users.status_pending')}</Badge>;
        if (v === 'accepted') return <Badge variant="success">{t('users.status_accepted')}</Badge>;
        return <Badge variant={row.isActive ? 'success' : 'preparing'}>{row.isActive ? t('common.active') : t('common.inactive')}</Badge>;
      },
    },
    {
      key: 'invitedBy',
      label: t('users.col_invited_by'),
      render: (v) => v
        ? <span className="text-slate-500 text-[12px]">{v.fullname || v.email}</span>
        : <span className="text-slate-300">—</span>,
    },
    {
      key: 'lastLoginAt',
      label: t('users.col_last_login'),
      render: (v, row) => row.invitationStatus === 'pending'
        ? <span className="text-slate-300 text-[12px]">—</span>
        : <span className="text-slate-500 text-[12px]">{fmtDate(v, locale)}</span>,
    },
    {
      key: '_act',
      label: '',
      align: 'right',
      render: (_, row) => hasPermission('manage_users') ? (
        <RowActions
          onEdit={() => openEdit(row)}
          onDelete={() => setConfirm(row)}
          deleteDisabled={String(row._id) === String(currentUser?._id)}
        />
      ) : null,
    },
  ], [t, locale, ROLE_META, currentUser, hasPermission]);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('users.title')}
        sub={t('users.count_other', { count: counts.all })}
        onRefresh={load}
        loading={loading}
        onAdd={hasPermission('manage_users') ? openAdd : undefined}
        addLabel={t('users.add_user')}
      />

      {error && <ErrorBanner message={error} onRetry={load} />}

      {!loading && counts.all > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <KpiCard label={t('users.kpi_total')}   value={counts.all}   icon={Users}     />
          <KpiCard label={t('users.kpi_active')}  value={counts.active}
            sub={counts.all ? `${Math.round(counts.active / counts.all * 100)} %` : '—'} />
          <KpiCard label={t('users.kpi_pending')} value={counts.pending} />
          <KpiCard label={t('users.kpi_admins')}  value={counts.admins}  icon={UserCheck} />
        </div>
      )}

      <FilterBar
        groups={[
          {
            value: roleFilter,
            onChange: (k) => { setRoleFilter(k); setPage(1); },
            tabs: [
              { key: 'all', label: t('users.filter_all'), count: counts.all },
              ...Object.entries(ROLE_META).map(([key, { label }]) => ({
                key,
                label,
                count: counts.byRole?.[key] ?? 0,
              })),
            ],
          },
        ]}
        search={search}
        onSearch={(e) => { setSearch(e.target.value); setPage(1); }}
        searchPlaceholder={t('users.search_placeholder')}
      />

      <Table
        columns={columns}
        rows={users}
        loading={loading}
        emptyState={
          <div className="py-16 flex flex-col items-center gap-3 text-slate-400">
            <Users size={32} strokeWidth={1.25} className="opacity-40" />
            <p className="text-[13px]">{t('users.empty')}</p>
          </div>
        }
        page={page}
        total={total}
        perPage={PER_PAGE}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />

      <Modal
        open={inviteModal}
        onClose={() => setInviteModal(false)}
        title={t('users.invite_title')}
        size="sm"
        footer={
          inviteSent ? (
            <button
              onClick={() => setInviteModal(false)}
              className="h-9 px-4 rounded-lg bg-primary text-white text-[13px] font-semibold hover:opacity-90 transition"
            >
              {t('common.close')}
            </button>
          ) : (
            <FormFooter onCancel={() => setInviteModal(false)} onSave={handleInvite} saving={inviting} />
          )
        }
      >
        {inviteSent ? (
          <div className="py-4 text-center flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-emerald-50 grid place-items-center">
              <span className="text-emerald-500 text-xl">✓</span>
            </div>
            <p className="text-[13px] text-slate-700 font-medium">
              {t('users.invite_sent')} <strong>{inviteData.email}</strong>
            </p>
            <p className="text-[12px] text-slate-400">{t('users.invite_sent_detail')}</p>
          </div>
        ) : (
          <>
            {inviteErrors._global && <GlobalError message={inviteErrors._global} />}
            <div className="grid gap-4">
              <FormField label={t('users.invite_email')} required helper={inviteErrors.email} error={!!inviteErrors.email}>
                <Input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => { setInviteData((d) => ({ ...d, email: e.target.value })); setInviteErrors((e2) => ({ ...e2, email: undefined })); }}
                  placeholder={t('users.invite_email_placeholder')}
                  state={inviteErrors.email ? 'error' : 'default'}
                />
              </FormField>
              <FormField label={t('users.invite_role')}>
                <Select
                  value={inviteData.role}
                  onChange={(e) => setInviteData((d) => ({ ...d, role: e.target.value }))}
                >
                  {Object.entries(ROLE_META).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </Select>
              </FormField>
            </div>
          </>
        )}
      </Modal>

      <Modal
        open={!!formModal}
        onClose={() => setFormModal(null)}
        title={t('users.edit_title')}
        size="md"
        footer={<FormFooter onCancel={() => setFormModal(null)} onSave={handleSave} saving={saving} />}
      >
        {formErrors._global && <GlobalError message={formErrors._global} />}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <FormField label={t('users.fullname_label')} required helper={formErrors.fullname} error={!!formErrors.fullname}>
              <Input
                value={formData.fullname}
                onChange={(e) => field('fullname', e.target.value)}
                placeholder={t('users.fullname_placeholder')}
                state={formErrors.fullname ? 'error' : 'default'}
              />
            </FormField>
          </div>
          <FormField label={t('users.invite_email')} required helper={formErrors.email} error={!!formErrors.email}>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => field('email', e.target.value)}
              placeholder={t('users.invite_email_placeholder')}
              state={formErrors.email ? 'error' : 'default'}
            />
          </FormField>
          <FormField label={t('users.initials_label')}>
            <Input value={formData.initials} onChange={(e) => field('initials', e.target.value)} placeholder="JD" />
          </FormField>
          <FormField label={t('users.title_label')}>
            <Input value={formData.title} onChange={(e) => field('title', e.target.value)} placeholder={t('users.title_placeholder')} />
          </FormField>
          <FormField label={t('users.invite_role')} required>
            <Select value={formData.role} onChange={(e) => field('role', e.target.value)}>
              {Object.entries(ROLE_META).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </Select>
          </FormField>
          <FormField label={t('users.lang_label')}>
            <Select value={formData.lang} onChange={(e) => field('lang', e.target.value)}>
              <option value="fr">{t('users.lang_fr')}</option>
              <option value="en">{t('users.lang_en')}</option>
            </Select>
          </FormField>
          <FormField label={t('users.status_label')}>
            <Select
              value={formData.isActive ? 'active' : 'inactive'}
              onChange={(e) => field('isActive', e.target.value === 'active')}
            >
              <option value="active">{t('common.active')}</option>
              <option value="inactive">{t('common.inactive')}</option>
            </Select>
          </FormField>
        </div>
      </Modal>

      <ConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleDelete}
        loading={saving}
        title={t('users.delete_title', { name: confirm?.fullname })}
        message={t('users.delete_message')}
        confirmLabel={t('users.delete_confirm')}
      />
    </div>
  );
}
